// Use-case pages (/use-cases and /use-cases/:slug). Read by the page template,
// scripts/prerender.mjs, scripts/verify-seo.mjs and the sitemap check, so the
// routes, metadata and visible copy stay in one place.
//
// Every claim must match what the product does today (see docs/gsc-opportunity-findings.md
// and deployment/api/analytics.mjs). Not live yet, so never claimed here:
// before/after outcome measurement, content decay history, scheduled reports,
// rank tracking, crawling, white-label reports.

export const USE_CASES_HUB = {
  path: "/use-cases",
  title: "SEO Signals use cases: what to fix next",
  description: "How SEO Signals uses Search Console and GA4 to find low-CTR pages, page-two rankings, overlapping pages and traffic that doesn't convert.",
  eyebrow: "USE CASES",
  h1: "What you can use SEO Signals for",
  lead: "SEO Signals reads your Google Search Console and Google Analytics 4 data and points to up to three fixes. These pages explain the situations it looks for, the evidence it shows, and where its limits are.",
};

export const USE_CASES = [
  {
    slug: "low-ctr-pages",
    nav: "Low-CTR pages",
    title: "Find Search Console pages with low CTR | SEO Signals",
    description: "Find pages that appear in Google but earn few clicks. SEO Signals compares each query's CTR with its position and shows the evidence before you rewrite.",
    eyebrow: "SEARCH CLICKS",
    h1: "Find pages that appear in Google but rarely get clicked",
    lead: "A page can rank and still be passed over. SEO Signals finds query and page pairs where the click-through rate is well below what that position usually earns, so you know which titles and descriptions to review first.",
    problem: {
      heading: "Impressions without clicks are easy to miss",
      body: "Search Console lists thousands of rows. A page with 10,000 impressions and 30 clicks looks fine in a total, but those searchers saw your result and chose another one. That gap is often the quickest win on a site, because the page already ranks.",
    },
    looksFor: [
      "Query and page pairs from your Search Console data for the selected period.",
      "Rows in positions 1 to 20 where the actual CTR is more than 30% below a published CTR-by-position benchmark for that position.",
      "Ranked by how far below the benchmark each row falls. Larger gaps on queries with more impressions are marked as higher severity.",
    ],
    example: {
      label: "Low-CTR opportunity",
      site: "Sample: Kingsway Dental",
      rows: [["Query", "emergency dentist southampton"], ["Avg. position", "4"], ["Impressions", "6,800"], ["Clicks", "61"], ["CTR", "0.9%"]],
      readout: "61 clicks from 6,800 impressions at position 4. Results in that position usually earn far more clicks, so the search result is worth reviewing.",
    },
    steps: [
      "Search the query yourself and look at the results above and below yours.",
      "Check whether your page title and meta description answer what the searcher wants.",
      "Rewrite the title or description, publish the change, and mark the fix in SEO Signals.",
    ],
    limits: [
      "The benchmark is an average. Ads, AI Overviews, maps and other features can lower clicks at any position.",
      "Google may show its own text instead of your meta description.",
      "SEO Signals points to the opportunity. It does not promise a specific traffic gain.",
    ],
    faqs: [
      { q: "What counts as a low CTR in SEO Signals?", a: "SEO Signals flags a query and page pair when its click-through rate is more than 30% below a published CTR-by-position benchmark for the same average position. The benchmark is a starting point for review, not a target." },
      { q: "Does SEO Signals rewrite my titles?", a: "No. SEO Signals shows the query, the page and the numbers. You decide what to change on your website, then track the fix in SEO Signals." },
    ],
    related: ["page-two-rankings", "search-console-and-ga4"],
  },
  {
    slug: "page-two-rankings",
    nav: "Page-two rankings",
    title: "Find pages ranking just off page one | SEO Signals",
    description: "See which pages sit at positions 11 to 20 in Google for queries with real impressions, ranked so you can choose which page to improve first.",
    eyebrow: "CLOSE TO PAGE ONE",
    h1: "Find the pages sitting just off page one",
    lead: "Pages at positions 11 to 20 already have Google's attention. SEO Signals pulls them out of your Search Console data and orders them by how much visibility is at stake, so you can choose one page to improve.",
    problem: {
      heading: "Page two gets impressions and almost no clicks",
      body: "Most searchers never reach the second page of results. A page averaging position 12 can collect thousands of impressions and very few visits. Moving it onto page one is often more realistic than ranking a brand new page.",
    },
    looksFor: [
      "Query and page pairs with an average position between 11 and 20.",
      "Ordered by impressions weighted by position, so a high-demand query at 11 comes before a quiet one at 19.",
      "The page that Google already associates with the query, so you improve the right URL.",
    ],
    example: {
      label: "Close to page one",
      site: "Sample: Solent Bike Repairs",
      rows: [["Query", "bike servicing near me"], ["Page", "/services/bike-service"], ["Avg. position", "12.4"], ["Impressions", "3,900"], ["Clicks", "22"]],
      readout: "3,900 impressions at an average position of 12.4. The page is close to page one for a query people search often.",
    },
    steps: [
      "Compare your page with the pages ranking in the top five for the query.",
      "Cover what they answer and you don't, and make the page's purpose clear in its title and headings.",
      "Link to the page from related pages on your site, then track the fix in SEO Signals.",
    ],
    limits: [
      "Average position blends many searches, locations and devices. Your own search may show something different.",
      "SEO Signals is not a rank tracker and does not check positions daily.",
      "Improving a page raises its chances. It does not guarantee a move onto page one.",
    ],
    faqs: [
      { q: "Why positions 11 to 20?", a: "Pages in positions 11 to 20 are on the second page of Google results. They already rank for the query, so improving them is usually faster than ranking a new page from nothing." },
      { q: "Is this the same as rank tracking?", a: "No. SEO Signals uses the average positions Google reports in Search Console for the period you select. It does not track individual keywords day by day." },
    ],
    related: ["low-ctr-pages", "keyword-cannibalisation"],
  },
  {
    slug: "keyword-cannibalisation",
    nav: "Overlapping pages",
    title: "Find pages competing for the same search | SEO Signals",
    description: "Find queries where Google shows two or more of your pages. SEO Signals lists the pages and combined impressions so you can check whether they compete.",
    eyebrow: "OVERLAPPING PAGES",
    h1: "Find queries where your own pages compete",
    lead: "When Google shows two of your pages for the same search, neither may perform as well as one clear page would. SEO Signals finds those queries in your Search Console data and shows which pages are involved.",
    problem: {
      heading: "Two similar pages can split one query",
      body: "Sites grow by adding pages, and over time two articles or a service page and a blog post can end up answering the same question. Google then has to choose between them, and the choice can change from week to week.",
    },
    looksFor: [
      "Queries where Search Console reports two or more of your pages in the same date range.",
      "The pages involved, their combined impressions and the spread between their positions.",
      "Sorted by combined impressions, so the biggest overlaps come first.",
    ],
    example: {
      label: "Pages appearing for one query",
      site: "Sample: Harbour Kitchens",
      rows: [["Query", "kitchen fitting cost"], ["Page 1", "/kitchen-fitting"], ["Page 2", "/blog/how-much-does-a-kitchen-cost"], ["Combined impressions", "5,200"]],
      readout: "Two pages appear for the same query in this period. Check whether they meet different needs before changing either one.",
    },
    steps: [
      "Open both pages and decide which one should answer the query.",
      "If they serve different needs, make each page's focus clearer and link between them.",
      "If they repeat each other, merge the content into the stronger page and redirect the other, then track the fix.",
    ],
    limits: [
      "Shared queries are normal, especially for broad terms. They are a reason to review, not proof of a problem.",
      "SEO Signals does not merge, redirect or edit pages for you.",
      "Search Console hides some low-volume queries, so small overlaps may not appear.",
    ],
    faqs: [
      { q: "Is keyword cannibalisation always bad?", a: "No. Two pages can rank for the same query when they meet different needs, such as a service page and a buying guide. SEO Signals shows the overlap so you can decide whether it is a problem." },
      { q: "How does SEO Signals detect it?", a: "SEO Signals looks for queries where Google Search Console reports two or more of your pages in the same date range, and shows the pages with their combined impressions." },
    ],
    related: ["page-two-rankings", "low-ctr-pages"],
  },
  {
    slug: "search-console-and-ga4",
    nav: "Search Console + GA4",
    title: "Search Console and GA4 together | SEO Signals",
    description: "See which pages win search visits but lead to few key events. SEO Signals reads Search Console and GA4 together so priorities reflect what visitors do.",
    eyebrow: "SEARCH + ANALYTICS",
    h1: "See which search traffic leads to action",
    lead: "Search Console shows how people find you. Google Analytics 4 shows what they do next. SEO Signals reads both, so a page that brings visitors but few enquiries, sign-ups or sales can become a priority too.",
    problem: {
      heading: "Two tools, two halves of the story",
      body: "Most SEO tools stop at rankings and clicks. Most analytics reports stop at sessions. A page can look like a success in one and a dead end in the other. Seeing both together tells you whether to work on getting the click or on what happens after it.",
    },
    looksFor: [
      "Landing pages with visits from your GA4 property and the key events you have set up.",
      "Landing pages with at least 500 sessions where fewer than 1 in 200 sessions records a key event.",
      "Search Console data for the same page, joined only when the host and path match exactly.",
    ],
    example: {
      label: "Visits without a next step",
      site: "Sample: Itchen Yoga Studio",
      rows: [["Page", "/blog/yoga-for-back-pain"], ["Sessions", "1,240"], ["Key events", "3"], ["Search clicks", "980"]],
      readout: "Most visits arrive from search, yet only 3 key events were recorded. Review the page's next step and your event setup before changing the content.",
    },
    steps: [
      "Check that your GA4 key events are set up and firing on this page.",
      "Look at what a visitor is asked to do next, and whether it is easy to find.",
      "Add or improve the next step, publish the change, and track the fix in SEO Signals.",
    ],
    limits: [
      "Key events must be configured in GA4. Without them, SEO Signals cannot tell whether a visit led to action.",
      "GA4 does not report the URL scheme, so SEO Signals does not merge http and https rows or guess at unclear matches.",
      "A low number of key events is a reason to review, not proof that the content is the problem.",
    ],
    faqs: [
      { q: "Do I need both Search Console and GA4?", a: "Yes. SEO Signals reads Google Search Console for search performance and Google Analytics 4 for visits and key events. Both connect with read-only access." },
      { q: "What are key events in GA4?", a: "Key events are the GA4 events you mark as important, such as a form submission, a booking or a purchase. SEO Signals uses them to see which pages lead to action." },
    ],
    related: ["low-ctr-pages", "small-business"],
  },
  {
    slug: "agencies",
    nav: "Agencies",
    title: "SEO priorities across client sites | SEO Signals for agencies",
    description: "See which client site needs attention first, with up to three evidence-based fixes per site and Markdown or PDF exports for client conversations.",
    eyebrow: "FOR AGENCIES AND FREELANCERS",
    h1: "Know which client site needs you first",
    lead: "When you look after several websites, the hard part is deciding where to spend the next hour. SEO Signals brings each site's Search Console and GA4 data into one overview and gives every site up to three evidence-based priorities.",
    problem: {
      heading: "Monthly reports don't tell you what to do on Monday",
      body: "Client reporting often means exporting the same charts every month. The work that moves results is choosing the right fix on the right site. SEO Signals keeps that decision in front of you, with the evidence you need to explain it to the client.",
    },
    looksFor: [
      "Every connected site in one overview, ordered by which one needs attention first. The order reflects crawl errors, pages that rarely convert, queries with few clicks and drops in search clicks.",
      "Up to three priorities per site, each with the query or page and the numbers behind it.",
      "Fix tracking per site, so you can see what has been done and what is next.",
    ],
    example: {
      label: "Which site needs you first",
      site: "Sample overview",
      rows: [["Northam Plumbing", "High: title worth rewriting"], ["Bitterne Florist", "Medium: page close to page one"], ["Shirley Physio", "Low: no new priority"]],
      readout: "Sites are ordered by the strength of their current priority, so you can start with the one most likely to benefit.",
    },
    steps: [
      "Connect the Google account that can view your clients' Search Console and GA4 properties.",
      "Open the overview and pick the site with the strongest priority.",
      "Review the evidence, make the change, and export the report as Markdown or PDF for the client.",
    ],
    limits: [
      "Access is read-only and uses the properties your Google account can already see.",
      "Reports are exported on demand. Scheduled and white-label reports are not available.",
      "The Agency plan covers up to 18 sites. Paid plans are coming soon.",
    ],
    faqs: [
      { q: "How many sites can an agency connect?", a: "The SEO Signals Agency plan covers up to 18 sites with 24 months of rolling data retention. Paid subscriptions are coming soon, and you can contact SEO Signals about early access." },
      { q: "Do my clients need their own SEO Signals account?", a: "No. You connect a Google account that already has access to your clients' Search Console and Google Analytics 4 properties." },
    ],
    related: ["small-business", "search-console-and-ga4"],
  },
  {
    slug: "small-business",
    nav: "Small businesses",
    title: "SEO priorities for small business owners | SEO Signals",
    description: "No SEO background needed. SEO Signals reads your Search Console and GA4 data and shows up to three fixes in plain English, with the evidence for each.",
    eyebrow: "FOR BUSINESS OWNERS",
    h1: "SEO help for business owners who don't have time for reports",
    lead: "You don't need to learn Search Console or Google Analytics to improve your website. SEO Signals reads both for you and shows up to three things worth fixing, explained in plain English.",
    problem: {
      heading: "The data is there. The time to read it isn't.",
      body: "Google already collects how people find your site and what they do next. Turning that into a decision takes hours most owners don't have. SEO Signals does the reading and gives you a short list to work through, one fix at a time.",
    },
    looksFor: [
      "Pages that appear in Google but get few clicks.",
      "Pages close to the first page of results.",
      "Pages that bring visitors who rarely enquire, book or buy.",
    ],
    example: {
      label: "Your next priority",
      site: "Sample: Bassett Bakery",
      rows: [["Priority", "Review your page title"], ["Why", "Seen 9,400 times in Google, clicked 41 times"], ["Next step", "Rewrite the title and description"]],
      readout: "Each priority says what to look at, why, and what to do next. You can ask a follow-up question in plain English.",
    },
    steps: [
      "Connect Search Console and Google Analytics with read-only access. Nothing on your site changes.",
      "Read your up to three priorities and the evidence behind them.",
      "Make the change yourself, or pass it to whoever runs your website, then mark it done.",
    ],
    limits: [
      "SEO Signals recommends changes. It never edits your website.",
      "When data is limited, you may see fewer than three priorities rather than invented ones.",
      "No tool can guarantee rankings. The priorities help you choose where to start.",
    ],
    faqs: [
      { q: "Do I need SEO experience to use SEO Signals?", a: "No. SEO Signals explains each priority in plain English and suggests a next step. You can also ask follow-up questions about your data." },
      { q: "Is SEO Signals free for small businesses?", a: "Connecting a site and viewing your first SEO priorities is free, and SEO Signals does not ask for a card. A demo with sample data is available without an account." },
    ],
    related: ["low-ctr-pages", "search-console-and-ga4"],
  },
];

export const findUseCase = slug => USE_CASES.find(u => u.slug === slug) || null;
export const pathForUseCase = slug => `/use-cases/${slug}`;
